/**
 * Data layers over generic edit field
 */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount } from 'wagmi';
import { ILimitOrder, IPriceAlert, ITicker } from '@kattana-io/common';
import {
  actionUpdateLimitOrderPrice,
  IUpdateLimitOrderPayload,
} from '../../../actions/limit_orders';
import { actionUpdateAlertPrice } from '../../../actions/price_alerts';
import GenericEditPanel from './generic';
import { authTokenSelector } from '../../../selectors/auth';
import { PRICE_FACTOR } from '../../Trading/reducers/dex/limitReducer';

import './styles.scss';
import { KattanaAPI } from '../../../store/createStore';

interface IPriceEditProps {
  alert: IPriceAlert;
  onCancel: () => void;
}

const PriceAlertsEdit = (props: IPriceEditProps) => {
  const dispatch = useDispatch();

  const onSave = (price: number) => {
    const field = props.alert.price > 0 ? 'price' : 'price_usd';
    props.alert[field] = price;
    dispatch(actionUpdateAlertPrice(props.alert));
  };

  return (
    <GenericEditPanel
      entity='Price alert'
      price={props.alert.price > 0 ? props.alert.price : props.alert.price_usd}
      onSave={onSave}
      onCancel={props.onCancel}
    />
  );
};

interface ILimitOrderEditProps {
  order: ILimitOrder;
  onCancel: () => void;
}

const LimitOrderEdit = ({ order, onCancel }: ILimitOrderEditProps) => {
  const dispatch = useDispatch();
  const { address: account } = useAccount();
  const [ticker, setTicker] = useState<ITicker>();
  const token = useSelector(authTokenSelector);

  useEffect(() => {
    KattanaAPI.tickers({ address: order.pair }).then((data) => {
      if (data) {
        setTicker(data[0]);
      }
    });
  }, [order]);

  const onSave = async (price: number) => {
    if (!ticker || !token) {
      return;
    }
    let rate = 0.0;

    if (!ticker.flipped) {
      rate = Math.ceil(ticker.price_a * PRICE_FACTOR) / PRICE_FACTOR;
    } else {
      rate = Math.ceil(ticker.price_b * PRICE_FACTOR) / PRICE_FACTOR;
    }

    const field = order.price > 0 ? 'price' : 'price_usd';
    order[field] = price;

    const payload: IUpdateLimitOrderPayload = {
      data: {
        uuid: order.uuid,
        wallet: account ?? '',
        data: {
          price: order.price,
          price_usd: order.price_usd,
          gt: rate < price,
        },
      },
      token,
    };
    dispatch(actionUpdateLimitOrderPrice(payload));
  };

  return (
    <GenericEditPanel
      entity='Limit order'
      price={order.price}
      onSave={onSave}
      onCancel={onCancel}
    />
  );
};

export { PriceAlertsEdit, LimitOrderEdit };
